import {
  ImportedFileDriverInfo,
  ImportedFileLogs,
  ImportedFileVehicleInfo,
  UploadedFileModel,
} from '@/app/models/import/uploadedFileModel'
import {
  CombinedFilesLogs,
  LogCounts,
  TempFileDriver,
  TempFilesLogs,
} from './types'
import {
  faClockDesk,
  faFileSpreadsheet,
  faUserQuestion,
} from '@fortawesome/pro-light-svg-icons'
import {
  faUserCheckRegular,
  faUserCheckSolid,
  faUserDashedRegular,
} from '@/lib/fa'
import { FileType } from 'app/constants/fileType'
import { Source } from 'app/constants/import/source'
import { getSource } from '@/app/services/import/sourceResolver'
import { faIdCard } from '@fortawesome/free-solid-svg-icons'
import { ImportBucket } from '@/modules/imports/model/imports.state.types'

export type ImportedFileLogsKey = keyof ImportedFileLogs

export const splitSelectedKey = (key: string) => {
  const [fileIndex, importedFileLogsKey, logIndex] = key.split('_')
  return {
    fileIndex: Number(fileIndex),
    importedFileLogsKey: importedFileLogsKey as keyof ImportedFileLogs,
    logIndex: Number(logIndex),
  }
}

export const selectedKey = (
  fileIndex: number,
  importedFileLogsKey: keyof ImportedFileLogs,
  logIndex: number,
) => `${fileIndex}_${importedFileLogsKey}_${logIndex}`

export const getSectionEntryType = (status: ImportedFileLogsKey) => {
  switch (status) {
    case 'existingDrivers':
    case 'ignoredDrivers':
    case 'newDrivers':
      return 'DRIVER'
    case 'newVehicles':
    case 'ignoredVehicles':
    case 'existingVehicles':
      return 'VEHICLE'
  }
}

export const getStatusLabel = (status: ImportedFileLogsKey | ImportBucket, t?: (key: string) => string) => {
  const tr = t ?? ((k: string) => k)
  switch (status) {
    case 'existing':
    case 'existingDrivers':
    case 'existingVehicles':
      return tr('common.active')
    case 'new':
    case 'newDrivers':
    case 'newVehicles':
      return tr('importModule.status-new')
    case 'ignored':
    case 'ignoredDrivers':
    case 'ignoredVehicles':
      return tr('common.inactive')
  }
}

export const getStatusUserIcon = (
  status: ImportedFileLogsKey | ImportBucket,
) => {
  switch (status) {
    case 'existing':
    case 'existingDrivers':
      return faUserCheckSolid
    case 'new':
    case 'newDrivers':
      return faUserQuestion
    case 'ignored':
    case 'ignoredDrivers':
      return faUserDashedRegular
  }
  return null
}

export const getSourceIcon = (source: Source) => {
  switch (source) {
    case Source.Card:
      return faIdCard
    case Source.File:
      return faClockDesk
    case Source.Spreadsheet:
      return faFileSpreadsheet
  }
  return null
}

export const getStatusPillClasses = (
  status: ImportedFileLogsKey | ImportBucket,
) => {
  switch (status) {
    case 'new':
    case 'newDrivers':
    case 'newVehicles':
      return 'tw-bg-blue-50 tw-text-blue-700'
    case 'existing':
    case 'existingDrivers':
    case 'existingVehicles':
      return 'tw-bg-green-50 tw-text-green-700'
    case 'ignored':
    case 'ignoredDrivers':
    case 'ignoredVehicles':
      return 'tw-bg-slate-100 tw-text-slate-600'
  }
}

const countSelectedItems = (items: { info: { checked?: boolean } }[]) =>
  items.filter((i) => i.info?.checked).length

export const countSelected = (unifiedLogs: CombinedFilesLogs): LogCounts => {
  const totalDrivers =
    unifiedLogs.existingDrivers.length +
    unifiedLogs.newDrivers.length +
    unifiedLogs.ignoredDrivers.length
  const selectedDrivers =
    countSelectedItems(unifiedLogs.existingDrivers) +
    countSelectedItems(unifiedLogs.newDrivers) +
    countSelectedItems(unifiedLogs.ignoredDrivers)
  const totalVehicles =
    unifiedLogs.existingVehicles.length +
    unifiedLogs.newVehicles.length +
    unifiedLogs.ignoredVehicles.length
  const selectedVehicles =
    countSelectedItems(unifiedLogs.existingVehicles) +
    countSelectedItems(unifiedLogs.newVehicles) +
    countSelectedItems(unifiedLogs.ignoredVehicles)

  return {
    totalDrivers,
    selectedDrivers,
    totalVehicles,
    selectedVehicles,
  }
}

export const transformCombinedLogsToApi = (
  combinedLogs: CombinedFilesLogs,
): ImportedFileLogs => {
  const selectedLogs: ImportedFileLogs = {
    existingDrivers: combinedLogs.existingDrivers
      .filter((l) => l.info.checked)
      .map((l) => l.info),
    ignoredDrivers: combinedLogs.ignoredDrivers
      .filter((l) => l.info.checked)
      .map((l) => l.info),
    newDrivers: combinedLogs.newDrivers
      .filter((l) => l.info.checked)
      .map((l) => l.info),
    existingVehicles: combinedLogs.existingVehicles
      .filter((l) => l.info.checked)
      .map((l) => l.info),
    ignoredVehicles: combinedLogs.ignoredVehicles
      .filter((l) => l.info.checked)
      .map((l) => l.info),
    newVehicles: combinedLogs.newVehicles
      .filter((l) => l.info.checked)
      .map((l) => l.info),
  }

  return selectedLogs
}

const getUniqueVehicleNumbers = (items: TempFileDriver[]) => {
  const vehicles: Record<string, number> = {}
  for (const item of items) {
    if (!Array.isArray(item.info.vehicles)) {
      break
    }
    for (const v of item.info.vehicles) {
      vehicles[v] = v in vehicles ? vehicles[v] + 1 : 0
    }
  }

  return Object.keys(vehicles)
}

export const extractCombinedLogsFromFiles = (
  files: UploadedFileModel[],
): CombinedFilesLogs => {
  const tempLogs: TempFilesLogs = {
    existingDrivers: {},
    ignoredDrivers: {},
    newDrivers: {},
    existingVehicles: {},
    ignoredVehicles: {},
    newVehicles: {},
  }

  for (const file of files) {
    for (const log of file.importedFileLogs.existingDrivers) {
      if (!(log.cardNumber in tempLogs.existingDrivers)) {
        tempLogs.existingDrivers[log.cardNumber] = []
      }

      tempLogs.existingDrivers[log.cardNumber].push({
        info: log,
        file: file,
      })
    }

    for (const log of file.importedFileLogs.ignoredDrivers) {
      if (!(log.cardNumber in tempLogs.ignoredDrivers)) {
        tempLogs.ignoredDrivers[log.cardNumber] = []
      }

      tempLogs.ignoredDrivers[log.cardNumber].push({
        info: log,
        file: file,
      })
    }

    for (const log of file.importedFileLogs.newDrivers) {
      if (!(log.cardNumber in tempLogs.newDrivers)) {
        tempLogs.newDrivers[log.cardNumber] = []
      }

      tempLogs.newDrivers[log.cardNumber].push({
        info: log,
        file: file,
      })
    }

    for (const log of file.importedFileLogs.existingVehicles) {
      if (!(log.registrationNumber in tempLogs.existingVehicles)) {
        tempLogs.existingVehicles[log.registrationNumber] = []
      }

      tempLogs.existingVehicles[log.registrationNumber].push({
        info: log,
        file: file,
      })
    }

    for (const log of file.importedFileLogs.ignoredVehicles) {
      if (!(log.registrationNumber in tempLogs.ignoredVehicles)) {
        tempLogs.ignoredVehicles[log.registrationNumber] = []
      }

      tempLogs.ignoredVehicles[log.registrationNumber].push({
        info: log,
        file: file,
      })
    }

    for (const log of file.importedFileLogs.newVehicles) {
      if (!(log.registrationNumber in tempLogs.newVehicles)) {
        tempLogs.newVehicles[log.registrationNumber] = []
      }

      tempLogs.newVehicles[log.registrationNumber].push({
        info: log,
        file: file,
      })
    }
  }

  const logs: CombinedFilesLogs = {
    existingDrivers: Object.entries(tempLogs.existingDrivers).map(
      ([id, items]) => {
        return {
          info: {
            fullName: items[0].info.fullName,
            cardNumber: items[0].info.cardNumber,
            daysRead: items.reduce((sum, item) => sum + item.info.daysRead, 0),
            checked: true,
            dateFrom: null,
            vehicles: getUniqueVehicleNumbers(items),
          },
          files: items.map((l) => l.file),
        }
      },
    ),
    ignoredDrivers: Object.entries(tempLogs.ignoredDrivers).map(
      ([id, items]) => {
        return {
          info: {
            fullName: items[0].info.fullName,
            cardNumber: items[0].info.cardNumber,
            daysRead: items.reduce((sum, item) => sum + item.info.daysRead, 0),
            checked: false,
            dateFrom: null,
            vehicles: getUniqueVehicleNumbers(items),
          },
          files: items.map((l) => l.file),
        }
      },
    ),
    newDrivers: Object.entries(tempLogs.newDrivers).map(([id, items]) => {
      return {
        info: {
          fullName: items[0].info.fullName,
          cardNumber: items[0].info.cardNumber,
          daysRead: items.reduce((sum, item) => sum + item.info.daysRead, 0),
          checked: false,
          dateFrom: null,
          vehicles: getUniqueVehicleNumbers(items),
        },
        files: items.map((l) => l.file),
      }
    }),
    existingVehicles: Object.entries(tempLogs.existingVehicles).map(
      ([id, items]) => {
        return {
          info: {
            registrationNumber: items[0].info.registrationNumber,
            daysRead: items.reduce((sum, item) => sum + item.info.daysRead, 0),
            checked: true,
          },
          files: items.map((l) => l.file),
        }
      },
    ),
    ignoredVehicles: Object.entries(tempLogs.ignoredVehicles).map(
      ([id, items]) => {
        return {
          info: {
            registrationNumber: items[0].info.registrationNumber,
            daysRead: items.reduce((sum, item) => sum + item.info.daysRead, 0),
            checked: false,
          },
          files: items.map((l) => l.file),
        }
      },
    ),
    newVehicles: Object.entries(tempLogs.newVehicles).map(([id, items]) => {
      return {
        info: {
          registrationNumber: items[0].info.registrationNumber,
          daysRead: items.reduce((sum, item) => sum + item.info.daysRead, 0),
          checked: false,
        },
        files: items.map((l) => l.file),
      }
    }),
  }

  return logs
}

export const uniqueFilesTypes = (files: { fileType: FileType }[]) => {
  const unique: FileType[] = []
  for (const file of files) {
    if (!unique.includes(file.fileType)) {
      unique.push(file.fileType)
    }
  }
  return unique
}

export const getUniqueSources = (files: { source: Source }[]): Source[] => {
  const unique: Source[] = []
  for (const file of files) {
    if (!unique.includes(file.source)) {
      unique.push(file.source)
    }
  }
  return unique
}

const getFileDisplaySource = (file: {
  source: Source
  fileType: FileType
}): Source => {
  if (file.fileType === FileType.Ddd || file.fileType === FileType.Chart) {
    return getSource(file.source)
  }
  return Source.Spreadsheet
}

export const getDisplaySources = (
  files: { source: Source; fileType: FileType }[],
): Source[] => {
  const sources = Array.from(new Set(files.map(getFileDisplaySource)))
  if (sources.includes(Source.Card)) {
    return sources.filter((s) => s !== Source.File)
  }
  return sources
}
